/**
 * GranulloBridgeSection.jsx — THE CANONICAL 5-STEP TRUST LOOP CHAPTER
 *
 * "What actually happens before money moves?"
 *
 * Interactive horizontal 5-node progression:
 * INTENT → CONDITIONS → EVIDENCE → DECISION → TWIN MEMORY
 *
 * Features:
 * - Clickable step progression with cinematic transitions
 * - ALLOWED / BLOCKED toggle changes Decision step state
 * - Proof drawer expansion on Evidence + Decision
 * - Hand-drawn annotations at proof moments
 * - Dark + light parity via canonical --g-* tokens
 * - Mobile vertical stack
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, CheckSquare, FileCheck, GitBranch, Lock,
  CheckCircle2, XCircle, Shield, Eye, ChevronRight,
} from 'lucide-react';
import { lensAnnotation, LENS_TYPES } from '../lens-v2/LensAnnotations';

const STEPS = [
  {
    id: 'intent',
    label: 'INTENT',
    icon: Target,
    color: '#3b82f6',
    headline: 'What money is supposed to do',
    example: 'Release scholarship funds only after attendance is verified',
    details: [
      'Organization declares disbursement purpose',
      'Program type inferred from intent structure',
      'Boundary conditions extracted automatically',
      'Rail requirements identified',
    ],
  },
  {
    id: 'conditions',
    label: 'CONDITIONS',
    icon: CheckSquare,
    color: '#f59e0b',
    headline: 'Live structural checks',
    example: 'Every condition must pass before money can move',
    checks: [
      { label: 'Identity verified', status: 'pass' },
      { label: 'Institution verified', status: 'pass' },
      { label: 'Amount within boundary', status: 'pass' },
      { label: 'Milestone complete', status: 'pass' },
      { label: 'Rail ready', status: 'pass' },
    ],
  },
  {
    id: 'evidence',
    label: 'EVIDENCE',
    icon: FileCheck,
    color: '#8b5cf6',
    headline: 'Immutable proof packet assembled',
    example: 'Every claim backed by verifiable evidence',
    proofs: [
      { label: 'Callback validated', hash: '0x8a4f...c3d1' },
      { label: 'Receipt hash', hash: '0x2b7e...91a0' },
      { label: 'Verification proof', hash: '0xf1c9...4e82' },
      { label: 'Audit packet sealed', hash: '0x6d3a...b7f5' },
      { label: 'Rule snapshot frozen', hash: '0x9e2c...d4a8' },
    ],
  },
  {
    id: 'decision',
    label: 'DECISION',
    icon: GitBranch,
    color: '#3D3D3D',
    headline: 'Structural determination',
    example: 'Not a recommendation. A structural verdict.',
  },
  {
    id: 'twin',
    label: 'TWIN MEMORY',
    icon: Lock,
    color: '#06b6d4',
    headline: 'Precedent created forever',
    example: 'This decision compounds into future intelligence',
    fields: [
      { label: 'Twin ID', value: 'GT-4A91-884F' },
      { label: 'Decision', value: 'ALLOWED' },
      { label: 'Evidence refs', value: '5 proofs sealed' },
      { label: 'Replay', value: 'Available for future simulation' },
      { label: 'Lineage', value: 'Branches from GT-3C72-11E0' },
    ],
  },
];

export default function GranulloBridgeSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [expandedProof, setExpandedProof] = useState(false);
  const step = STEPS[activeStep];

  return (
    <section
      className="relative py-16 md:py-24"
      style={{ background: 'var(--surface-bg, #111111)', color: 'var(--text-primary, #0A0A0A)' }}
      data-testid="granullo-bridge-section"
      {...lensAnnotation({
        id: 'trust-loop-chapter',
        type: LENS_TYPES.STRUCTURAL_PROOF,
        title: 'The 5-Step Trust Loop',
        meaning: 'Every payment passes through 5 structural gates before movement. This is the core Granullo moat.',
        actions: ['Step through trust loop', 'Toggle allowed/blocked', 'Inspect evidence packet'],
        relations: ['payment-twin', 'consequence-memory', 'rule-enforcement'],
        state: 'doctrine-active',
        priority: 'high',
      })}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--text-muted, #6B6B6B)' }}>
            How Granullo Works
          </p>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight mb-4" style={{ color: 'var(--text-primary, #0A0A0A)', letterSpacing: '-0.02em' }}>
            What actually happens{' '}
            <span style={{
              fontFamily: '"Permanent Marker", cursive',
              color: '#CD2200',
              fontSize: '0.93em',
              display: 'inline-block',
              transform: 'rotate(-0.5deg)',
            }}>before</span>{' '}
            money moves?
          </h2>
          <p className="text-[13px] max-w-[480px] mx-auto" style={{ color: 'var(--shell-text-muted)' }}>
            Granullo doesn't observe money after movement.
            It structurally determines whether money is allowed to move at all.
          </p>
        </motion.div>

        {/* ═══ HORIZONTAL 5-NODE TRUST LOOP ═══ */}
        <div className="mb-10 overflow-x-auto pb-2" data-testid="trust-loop-nodes">
          <div className="flex items-center justify-center gap-0 min-w-[640px] px-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = activeStep === i;
              const isPast = activeStep > i;
              const nodeColor = isActive || isPast ? s.color : '#3D3D3D';
              return (
                <React.Fragment key={s.id}>
                  {i > 0 && (
                    <motion.div
                      className="h-px flex-shrink-0"
                      style={{ width: 32, background: isPast ? STEPS[i - 1].color + '60' : '#1E293B' }}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                    />
                  )}
                  <button
                    onClick={() => setActiveStep(i)}
                    className="flex flex-col items-center flex-shrink-0 cursor-pointer group transition-all"
                    data-testid={`trust-loop-step-${s.id}`}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-2 transition-all duration-300"
                      style={{
                        background: isActive ? `${s.color}15` : isPast ? `${s.color}08` : 'transparent',
                        border: `1.5px solid ${isActive ? s.color : isPast ? s.color + '40' : '#1E293B'}`,
                        boxShadow: isActive ? `0 0 16px ${s.color}20` : 'none',
                      }}
                    >
                      <Icon size={16} style={{ color: nodeColor }} />
                    </div>
                    <span
                      className="text-[9px] font-mono uppercase tracking-wider whitespace-nowrap"
                      style={{ color: isActive ? s.color : isPast ? s.color + '80' : '#555555', fontWeight: isActive ? 600 : 400 }}
                    >
                      {s.label}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ═══ STEP DETAIL PANEL ═══ */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${step.id}-${isBlocked}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
            className="max-w-[680px] mx-auto rounded-xl overflow-hidden"
            style={{ background: 'var(--g-surface, #1A1A1A)', border: '1px solid var(--border-soft, rgba(148,163,184,0.1))' }}
            data-testid="trust-loop-detail"
          >
            {/* Panel header */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(148,163,184,0.06)' }}>
              <div className="flex items-center gap-2">
                <step.icon size={13} style={{ color: step.color }} />
                <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: step.color }}>{step.label}</span>
              </div>
              <span className="text-[9px] font-mono" style={{ color: '#555555' }}>step {activeStep + 1}/5</span>
            </div>

            {/* Panel content */}
            <div className="p-5">
              <h3 className="text-[15px] font-semibold mb-1" style={{ color: 'var(--text-primary, #E2E8F0)' }}>{step.headline}</h3>
              <p className="text-[11px] mb-4" style={{ color: 'var(--shell-text-muted)' }}>{step.example}</p>

              {/* INTENT step */}
              {step.id === 'intent' && (
                <div className="space-y-1.5">
                  {step.details.map((d, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                      className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: step.color }} />
                      <span className="text-[11px]" style={{ color: 'var(--shell-text-muted)' }}>{d}</span>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* CONDITIONS step */}
              {step.id === 'conditions' && (
                <div className="space-y-2">
                  {step.checks.map((check, i) => {
                    const blocked = isBlocked && i === 3;
                    return (
                      <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                        className="flex items-center justify-between p-2.5 rounded-lg"
                        style={{ background: blocked ? 'rgba(239,68,68,0.06)' : 'rgba(5,150,105,0.04)', border: `1px solid ${blocked ? 'rgba(239,68,68,0.15)' : 'rgba(5,150,105,0.1)'}` }}>
                        <span className="text-[11px]" style={{ color: blocked ? '#FCA5A5' : '#999999' }}>{check.label}</span>
                        {blocked ? <XCircle size={13} style={{ color: '#EF4444' }} /> : <CheckCircle2 size={13} style={{ color: '#22C55E' }} />}
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* EVIDENCE step — with expandable proof drawer */}
              {step.id === 'evidence' && (
                <div>
                  <button onClick={() => setExpandedProof(!expandedProof)}
                    className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider mb-3 cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ color: step.color }}
                    data-testid="evidence-drawer-toggle">
                    <Eye size={11} /> {expandedProof ? 'Collapse proof packet' : 'Expand proof packet'}
                    <ChevronRight size={11} style={{ transform: expandedProof ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                  </button>
                  <AnimatePresence>
                    {expandedProof && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }} className="space-y-1.5">
                        {step.proofs.map((proof, i) => (
                          <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                            className="flex items-center justify-between p-2.5 rounded-lg"
                            style={{ background: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.1)' }}>
                            <div className="flex items-center gap-2">
                              <Shield size={11} style={{ color: '#A78BFA' }} />
                              <span className="text-[11px]" style={{ color: '#C4B5FD' }}>{proof.label}</span>
                            </div>
                            <span className="text-[9px] font-mono" style={{ color: '#6B6B6B' }}>{proof.hash}</span>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {!expandedProof && (
                    <div className="text-[10px] font-mono" style={{ color: '#6B6B6B' }}>{step.proofs.length} proofs sealed and hash-verified</div>
                  )}
                </div>
              )}

              {/* DECISION step — ALLOWED / BLOCKED toggle */}
              {step.id === 'decision' && (
                <div>
                  {/* Toggle */}
                  <div className="flex items-center gap-3 mb-5" data-testid="decision-toggle">
                    <button onClick={() => setIsBlocked(false)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                      style={{
                        background: !isBlocked ? 'rgba(5,150,105,0.1)' : 'transparent',
                        border: `1px solid ${!isBlocked ? '#3D3D3D' : '#1E293B'}`,
                        color: !isBlocked ? '#22C55E' : '#6B6B6B',
                      }}>
                      <CheckCircle2 size={12} /> ALLOWED
                    </button>
                    <button onClick={() => setIsBlocked(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                      style={{
                        background: isBlocked ? 'rgba(239,68,68,0.1)' : 'transparent',
                        border: `1px solid ${isBlocked ? '#EF4444' : '#1E293B'}`,
                        color: isBlocked ? '#FCA5A5' : '#6B6B6B',
                      }}>
                      <XCircle size={12} /> BLOCKED
                    </button>
                  </div>

                  {/* Decision result */}
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="p-4 rounded-xl text-center"
                    style={{
                      background: isBlocked ? 'rgba(239,68,68,0.06)' : 'rgba(5,150,105,0.06)',
                      border: `1.5px solid ${isBlocked ? 'rgba(239,68,68,0.2)' : 'rgba(5,150,105,0.2)'}`,
                    }}
                    data-testid="decision-result"
                  >
                    <div className="text-xl font-semibold font-mono mb-1" style={{ color: isBlocked ? '#EF4444' : '#22C55E' }}>
                      {isBlocked ? 'BLOCKED' : 'ALLOWED'}
                    </div>
                    <p className="text-[11px] mb-3" style={{ color: 'var(--shell-text-muted)' }}>
                      {isBlocked
                        ? 'Milestone condition not met. Money cannot move until attendance verification is complete.'
                        : 'All 5 conditions passed. Evidence packet sealed. Money is structurally cleared for movement.'}
                    </p>
                    {/* Hand-drawn annotation */}
                    <span className="text-[10px] inline-block" style={{
                      fontFamily: '"Permanent Marker", cursive',
                      color: isBlocked ? '#EF4444' : '#CD2200',
                      transform: 'rotate(-1deg)',
                    }}>
                      {isBlocked ? '← what needs to change' : '← structural certainty'}
                    </span>
                  </motion.div>

                  {isBlocked && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 text-center">
                      <button className="text-[11px] font-medium px-4 py-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{ background: 'rgba(239,68,68,0.08)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.15)' }}
                        data-testid="blocked-cta">
                        What needs to change?
                      </button>
                    </motion.div>
                  )}
                </div>
              )}

              {/* TWIN MEMORY step */}
              {step.id === 'twin' && (
                <div>
                  <div className="space-y-1.5 mb-4">
                    {step.fields.map((field, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                        className="flex items-center justify-between p-2 rounded-lg"
                        style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.08)' }}>
                        <span className="text-[10px] font-mono" style={{ color: '#6B6B6B' }}>{field.label}</span>
                        <span className="text-[11px] font-medium" style={{ color: i === 1 ? (isBlocked ? '#EF4444' : '#22C55E') : '#E2E8F0' }}>
                          {i === 1 ? (isBlocked ? 'BLOCKED' : 'ALLOWED') : field.value}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                  {/* Compounding pulse */}
                  <div className="p-3 rounded-lg text-center" style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.1)' }}>
                    <span className="text-[10px] inline-block" style={{
                      fontFamily: '"Permanent Marker", cursive',
                      color: '#CD2200',
                      transform: 'rotate(-0.5deg)',
                    }}>
                      this decision compounds into all future decisions
                    </span>
                    <p className="text-[9px] font-mono mt-1" style={{ color: '#555555' }}>
                      Future twins branch from this evidence. Nothing is forgotten.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation arrows */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(148,163,184,0.06)' }}>
              <button
                onClick={() => activeStep > 0 && setActiveStep(activeStep - 1)}
                className="text-[10px] font-mono uppercase tracking-wider transition-opacity"
                style={{ color: activeStep > 0 ? '#999999' : '#1E293B', cursor: activeStep > 0 ? 'pointer' : 'default' }}
                data-testid="trust-loop-prev"
              >
                Previous
              </button>
              <button
                onClick={() => activeStep < 4 && setActiveStep(activeStep + 1)}
                className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider transition-opacity"
                style={{ color: activeStep < 4 ? step.color : '#1E293B', cursor: activeStep < 4 ? 'pointer' : 'default' }}
                data-testid="trust-loop-next"
              >
                {activeStep < 4 ? 'Next' : 'Complete'} <ChevronRight size={11} />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
